import { DurationUnitType } from 'dayjs/plugin/duration';
import * as dayjs from 'dayjs';
import * as advancedFormat from 'dayjs/plugin/advancedFormat';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import { GlobalConstants } from '../constant/constant';
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export type DateTimeType = string | dayjs.Dayjs | Date;

export class DateTimeHelper {
  /**
   * Returns the current date and time.
   * @returns The current date and time.
   */
  public static getDateTime() {
    return dayjs();
  }

  /**
   * Returns the time zone abbreviation for a given datetime and time zone ID.
   * @param datetime - The dateTime value to get the time zone abbreviation for.
   * @param timeZoneId - The IANA ID of the time zone.
   * @returns The time zone abbreviation.
   */
  public static getTimeZoneAbbreviation(datetime: DateTimeType, timeZoneId: string): string {
    return dayjs.tz(datetime, timeZoneId).format('z');
  }

  /**
   * Returns the time zone ID.
   * @returns The time zone ID.
   */
  public static getTimeZoneId(): string {
    return dayjs.tz.guess();
  }

  /**
   * Checks if the given dateTime has an offset specified.
   * @param dateTime - The dateTime to check. It can be a string, a Date object, or a dayjs object.
   * @returns A boolean indicating whether the date and time has an offset specified.
   */
  public static checkHasOffsetSpecified(dateTime: DateTimeType): boolean {
    const hasOffset = (str: string) => /[+-]\d{2}:\d{2}$/.test(str);

    if (typeof dateTime === 'string') {
      return hasOffset(dateTime);
    }

    if (dateTime instanceof Date) {
      return hasOffset(dateTime.toISOString());
    }

    if (dayjs.isDayjs(dateTime)) {
      return true;
    }
  }

  /**
   * Checks if the given dateTime is a valid date and time.
   * @param dateTime - The dateTime to check. It can be a string, a Date object, or a dayjs object.
   * @returns A boolean indicating whether the date and time is valid.
   */
  public static checkIsValidDateTime(dateTime: DateTimeType): boolean {
    return dayjs(dateTime).isValid();
  }

  /**
   * Returns the offset from UTC in minutes for the given datetime.
   * @param dateTime - The dateTime value to calculate the offset from.
   * @param timeZoneId - The time zone ID to use for the calculation.
   * @returns The offset from UTC in minutes.
   * @throws {Error} If dateTime does not include an offset.
   */
  public static getUtcOffsetInMinutes(dateTime: DateTimeType, timeZoneId: string): number {
    const timeInTimeZone = dayjs.tz(dateTime, timeZoneId);
    const offsetInMinutes = timeInTimeZone.utcOffset();
    return offsetInMinutes;
  }

  /**
   * Returns the current date and time in UTC.
   * @returns {dayjs.Dayjs} The current date and time in the user's in UTC.
   */
  public static getUtcNow(): dayjs.Dayjs {
    const now = dayjs().utc();
    return now.utcOffset(0, true);
  }

  /**
   * Returns the current date and time in the user's current timezone.
   * @returns {dayjs.Dayjs} The current date and time in the user's current timezone.
   */
  public static getLocalNow(): dayjs.Dayjs {
    const now = dayjs();
    return now.utcOffset(now.utcOffset(), true);
  }

  /**
   * Returns the current UTC date and time as a formatted string.
   * @returns The formatted UTC date and time string.
   */
  public static getUtcNowString(): string {
    return this.formatDateTime(this.getUtcNow());
  }

  /**
   * Returns the current local date and time as a formatted string.
   * @returns The formatted local date and time string.
   */
  public static getLocalNowString(): string {
    return this.formatDateTime(this.getLocalNow());
  }

  public static getDifference(dateTime1: DateTimeType, dateTime2: DateTimeType, unit: dayjs.OpUnitType): number {
    return dayjs(dateTime1).diff(dayjs(dateTime2), unit);
  }

  /**
   * Formats a given date or dayjs object to a string representation.
   * @param dateTime - The string, dayjs, or date object to be formatted.
   * @param timeZoneId - The time zone ID to use for formatting. Defaults to the user's current time zone.
   * @param format - The format to use for the date and time string. Defaults to 'YYYY-MM-DDTHH:mm:ss.SSS'.
   * @returns The formatted date and time as a string.
   */
  public static formatDateTime(dateTime: DateTimeType, format = 'YYYY-MM-DDTHH:mm:ss'): string {
    return dayjs(dateTime).format(format);
  }

  /**
   * Formats a given datetime to a string representation of its date only (time values set to 0).
   * @param dateTime - The string, dayjs, or date object to be formatted.
   * @returns The formatted date and time as a string.
   */
  public static formatDateTimeAsDate(dateTime: DateTimeType): string {
    return dayjs(dateTime).format('YYYY-MM-DDT00:00:00');
  }

  /**
   * Checks if two dateTime values are in the same 24-hour period.
   * @param dateTime1 - The first dateTime value to compare.
   * @param timeZoneId1 - Optional. The time zone ID of the first dateTime value. Defaults to 'UTC'.
   * @param dateTime2 - Optional. The second dateTime value to compare. Defaults to the current UTC time.
   * @param timeZoneId2 - Optional. The time zone ID of the second dateTime value. Defaults to 'UTC'.
   * @returns A boolean value indicating whether the two dateTime values are in the same 24-hour period.
   */
  public static checkInSame24Hours(dateTime1: DateTimeType, timeZoneId1: string = 'UTC', dateTime2: DateTimeType = this.getUtcNow(), timeZoneId2: string = 'UTC'): boolean {
    return this.checkTimeDifferenceLessThan('hour', 24, dateTime1, timeZoneId1, dateTime2, timeZoneId2);
  }

  /**
   * Checks if two dateTime values are on the same day.
   * @param dateTime1 - The first dateTime value to compare.
   * @param dateTime2 - Optional. The second dateTime value to compare. Defaults to the current UTC time.
   * @param midnightAllowed - Optional. A boolean value indicating whether the following midnight is allowed. Defaults to false.
   * @returns A boolean value indicating whether the two dateTime values are on the same day.
   */
  public static checkOnSameDay(dateTime1: DateTimeType, dateTime2: DateTimeType, midnightAllowed = false): boolean {
    if (!midnightAllowed) {
      return dayjs(dateTime1).isSame(dateTime2, 'day');
    } else {
      const nextDayMidnight = this.getStartOfNextDay(dateTime1);
      return dayjs(dateTime1).isSame(dateTime2, 'day') || dayjs(dateTime2).isSame(nextDayMidnight);
    }
  }

  /**
   * Checks if two dateTime values are the same.
   * @param dateTime1 - The first dateTime value to compare.
   * @param timeZoneId1 - Optional. The time zone ID of the first dateTime value. Defaults to 'UTC'.
   * @param dateTime2 - The second dateTime value to compare.
   * @param timeZoneId2 - Optional. The time zone ID of the second dateTime value. Defaults to 'UTC'.
   * @param unit - The unit of time to compare. Defaults to 'milliseconds'.
   * @returns A boolean value indicating whether the two dateTime values are the same.
   */
  public static checkIsSame(dateTime1: DateTimeType, timeZoneId1: string = 'UTC', dateTime2: DateTimeType, timeZoneId2: string = 'UTC', unit: dayjs.OpUnitType = 'millisecond'): boolean {
    const dt1 = dayjs.tz(dateTime1, timeZoneId1);
    const dt2 = dayjs.tz(dateTime2, timeZoneId2);
    return dt1.isSame(dt2, unit);
  }

  /**
   * Checks if the given dateTime is today.
   * @param dateTime - The dateTime value to check.
   * @returns A boolean value indicating whether the dateTime is today.
   */
  public static checkIsToday(dateTime: DateTimeType): boolean {
    return dayjs(dateTime).isToday();
  }

  /**
   * Checks if time difference between two dateTime values is less than the threshold specified.
   * @param unit - The unit of time to compare.
   * @param differenceThreshold - The threshold difference in the specified unit.
   * @param dateTime1 - The first dateTime value to compare.
   * @param timeZoneId1 - Optional. The time zone ID of the first dateTime value. Defaults to 'UTC'.
   * @param dateTime2 - Optional. The second dateTime value to compare. Defaults to the current UTC time.
   * @param timeZoneId2 - Optional. The time zone ID of the second dateTime value. Defaults to 'UTC'.
   * @returns A boolean value indicating whether the time difference between the two dateTime values is less than the threshold specified.
   */
  public static checkTimeDifferenceLessThan(
    unit: dayjs.OpUnitType,
    differenceThreshold: number,
    dateTime1: DateTimeType,
    timeZoneId1: string = 'UTC',
    dateTime2: DateTimeType = this.getUtcNow(),
    timeZoneId2: string = 'UTC'
  ): boolean {
    if (!dateTime1 || !dateTime2) {
      console.log('Warning: a datetime given for comparison was null.');
      return false;
    }
    const dt1 = dayjs.tz(dateTime1, timeZoneId1);
    const dt2 = dayjs.tz(dateTime2, timeZoneId2);
    const differenceInUnit = Math.abs(dt1.diff(dt2, unit));
    return differenceInUnit < differenceThreshold;
  }

  /**
   * Checks if the first dateTime value is after the second dateTime value.
   * @param timeZoneId1 - Optional. The time zone ID of the first dateTime value. Defaults to 'UTC'.
   * @param dateTime2 - The second dateTime value to compare.
   * @param timeZoneId2 - Optional. The time zone ID of the second dateTime value. Defaults to 'UTC'.
   * @returns A boolean value indicating whether the first dateTime value is after the second dateTime value.
   */
  public static checkTimeIsEqual(dateTime1: DateTimeType, timeZoneId1: string = 'UTC', dateTime2: DateTimeType, timeZoneId2: string = 'UTC'): boolean {
    if (!dateTime1 || !dateTime2) {
      console.log('Warning: a datetime given for comparison was null.');
      return false;
    }
    const dt1 = dayjs.tz(dateTime1, timeZoneId1);
    const dt2 = dayjs.tz(dateTime2, timeZoneId2);
    return dt1.isSame(dt2);
  }

  public static checkTimeIsAfter(dateTime1: DateTimeType, timeZoneId1: string = 'UTC', dateTime2: DateTimeType, timeZoneId2: string = 'UTC', isInclusive: boolean = false): boolean {
    if (!dateTime1 || !dateTime2) {
      console.log('Warning: a datetime given for comparison was null.');
      return false;
    }
    const dt1 = dayjs.tz(dateTime1, timeZoneId1);
    const dt2 = dayjs.tz(dateTime2, timeZoneId2);
    return isInclusive ? dt1.isSameOrAfter(dt2) : dt1.isAfter(dt2);
  }

  public static checkTimeIsBefore(dateTime1: DateTimeType, timeZoneId1: string = 'UTC', dateTime2: DateTimeType, timeZoneId2: string = 'UTC', isInclusive: boolean = false): boolean {
    if (!dateTime1 || !dateTime2) {
      console.log('Warning: a datetime given for comparison was null.');
      return false;
    }
    const dt1 = dayjs.tz(dateTime1, timeZoneId1);
    const dt2 = dayjs.tz(dateTime2, timeZoneId2);
    return isInclusive ? dt1.isSameOrBefore(dt2) : dt1.isBefore(dt2);
  }

  /**
   * Converts a given dateTime from one time zone to another.
   * @param dateTime - The dateTime to convert.
   * @param fromTimeZoneId - The IANA time zone ID of the original dateTime.
   * @param toTimeZoneId - The IANA time zone ID of the target time zone.
   * @returns The converted dateTime in the target time zone.
   */
  public static convertTimeZone(dateTime: DateTimeType, fromTimeZoneId: string, toTimeZoneId: string): dayjs.Dayjs {
    const dateTimeInFromTimeZone = dayjs.tz(dateTime, fromTimeZoneId);
    return dateTimeInFromTimeZone.tz(toTimeZoneId);
  }

  /**
   * Converts a given time string to a dayjs object.
   * @param time - The time string to convert.
   * @param datetime - The dayjs object to use for the dayjs object. Defaults to the current date and time.
   * @param timeZoneId - The time zone ID to use for the dayjs object. Defaults to 'UTC'.
   * @returns The dayjs object created from the time string.
   */
  public static convertTimeToDatetime(time: string, datetime: DateTimeType = dayjs(), timeZoneId: string = 'UTC'): dayjs.Dayjs {
    return dayjs.tz(`${dayjs(datetime).format('YYYY-MM-DD')} ${time}`, timeZoneId);
  }

  /**
   * Convert an ambiguous DateTimeType (string, Date, or dayjs) to a native JS Date interpreted in the given IANA time zone.
   */
  public static convertDatetimeTypeToDateType(datetime: DateTimeType, timeZoneId: string = 'UTC'): Date {
    return dayjs.tz(datetime, timeZoneId).toDate();
  }

  /**
   * Returns the difference between two dateTime values.
   * @param dateTime1 - The first dateTime value to compare.
   * @param timeZoneId1 - Optional. The time zone ID of the first dateTime value. Defaults to 'UTC'.
   * @param dateTime2 - The second dateTime value to compare.
   * @param timeZoneId2 - Optional. The time zone ID of the second dateTime value. Defaults to 'UTC'.
   * @param unit - Optional. The unit of time to return the difference in. Defaults to 'milliseconds'.
   * @returns The difference between the two dateTime values.
   */
  public static getTimeDuration(dateTime1: DateTimeType, timeZoneId1: string = 'UTC', dateTime2: DateTimeType, timeZoneId2: string = 'UTC', unit: DurationUnitType = 'milliseconds'): plugin.Duration {
    if (!dateTime1 || !dateTime2) {
      console.log('Warning: a datetime given for comparison was null.');
      return;
    }

    const dt1 = dayjs.tz(dateTime1, timeZoneId1);
    const dt2 = dayjs.tz(dateTime2, timeZoneId2);
    let diff = dt2.diff(dt1);
    return dayjs.duration(diff, unit);
  }

  /**
   * Formats a duration object to a string representation.
   * @param duration - The duration object to be formatted.
   * @param format - Optional. The format to use for the duration string. Defaults to 'H.M'.
   * @returns The formatted duration as a string.
   */
  public static formatDuration(duration: plugin.Duration, format: 'H.M' | 'HHh MMm' | 'Hh Mm' = 'H.M'): string {
    const totalMinutes = Math.floor(duration.asMinutes());
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (format === 'H.M') {
      //e.g. 8.25
      return duration.asHours().toFixed(2);
    } else if (format === 'HHh MMm') {
      //e.g. 08h 15m
      return `${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`;
    } else if (format === 'Hh Mm') {
      //e.g. 8h 15m
      return `${hours}h ${minutes}m`;
    }
  }

  /**
   * Rounds a given dateTime to the nearest interval specified.
   * For interval=1, uses floor rounding (truncates seconds) so the time doesn't jump ahead of the system clock.
   * For larger intervals, uses nearest rounding.
   * @param dateTime - The dateTime value to round.
   * @param interval - The interval to round to in minutes.
   * @returns The rounded dateTime.
   */
  public static roundDateTime(dateTime: DateTimeType, interval: number): dayjs.Dayjs {
    const roundFunc = interval <= 1 ? Math.floor : Math.round;
    return dayjs(roundFunc(dayjs(dateTime).toDate().getTime() / (interval * GlobalConstants.ONE_MINUTE_MS)) * (interval * GlobalConstants.ONE_MINUTE_MS));
  }

  /**
   * Rounds a given dateTime to the nearest interval specified.
   * @param dateTime - The dateTime value to round.
   * @param minutes - The interval to round to in minutes,
   * @returns The rounded dateTime.
   */
  public static roundDateTimeToNearestMinutes(dateTime: DateTimeType, minutes: number): dayjs.Dayjs {
    const time = dayjs(dateTime);
    const roundedMinute = Math.round(time.minute() / minutes) * minutes;
    return time.minute(roundedMinute).second(0);
  }

  /**
   * Returns the start of the day for the given dateTime.
   * @param dateTime - The dateTime value to get the start of the day for.
   * @returns The start of the day for the given dateTime.
   */
  public static getStartOfDay(dateTime: DateTimeType): dayjs.Dayjs {
    return dayjs(dateTime).startOf('day');
  }

  /**
   * Returns the end of the day for the given dateTime.
   * @param dateTime - The dateTime value to get the end of the day for.
   * @returns The end of the day for the given dateTime.
   */
  public static getEndOfDay(dateTime: DateTimeType): dayjs.Dayjs {
    return dayjs(dateTime).endOf('day');
  }

  /**
   * Returns the midnight of the next day for the given dateTime.
   * @param dateTime - The dateTime value to get the start of the day for.
   * @returns The start of the day for the given dateTime.
   */
  public static getStartOfNextDay(dateTime: DateTimeType): dayjs.Dayjs {
    return dayjs(dateTime).add(1, 'day').startOf('day');
  }

  /**
   * Checks if the targetDateTime is between the startDateTime and endDateTime.
   * @param targetDateTime - The target dateTime value to check.
   * @param targetTimeZoneId - The time zone ID of the target dateTime value.
   * @param startDateTime - The start dateTime value of the range.
   * @param startDateTimeZoneId - The time zone ID of the start dateTime value.
   * @param endDateTime - The end dateTime value of the range.
   * @param endDateTimeZoneId - The time zone ID of the end dateTime value.
   * @param isInclusive - Optional. A boolean value indicating whether the start and end times are inclusive. Defaults to false.
   * @returns A boolean value indicating whether the targetDateTime is between the startDateTime and endDateTime.
   */
  public static checkTimeIsBetween(
    targetDateTime: DateTimeType,
    targetTimeZoneId: string,
    startDateTime: DateTimeType,
    startDateTimeZoneId: string,
    endDateTime: DateTimeType,
    endDateTimeZoneId: string,
    isInclusive: boolean = false
  ): boolean {
    return (
      this.checkTimeIsAfter(targetDateTime, targetTimeZoneId, startDateTime, startDateTimeZoneId, isInclusive) &&
      this.checkTimeIsBefore(targetDateTime, targetTimeZoneId, endDateTime, endDateTimeZoneId, isInclusive)
    );
  }

  /**
   * Checks if two time ranges overlap.
   * @param startDateTime1 - The start dateTime value of the first range.
   * @param startDateTimeZoneId1 - The time zone ID of the start dateTime value of the first range.
   * @param endDateTime1 - The end dateTime value of the first range.
   * @param endDateTimeZoneId1 - The time zone ID of the end dateTime value of the first range.
   * @param startDateTime2 - The start dateTime value of the second range.
   * @param endDateTime2 - The end dateTime value of the second range.
   * @param endDateTimeZoneId2 - The time zone ID of the end dateTime value of the second range.
   * @param isInclusive - Optional. A boolean value indicating whether the start and end times are inclusive. Defaults to false.
   * @returns A boolean value indicating whether the two time ranges overlap.
   */
  public static checkTimeRangesOverlap(
    startDateTime1: DateTimeType,
    startDateTimeZoneId1: string,
    endDateTime1: DateTimeType,
    endDateTimeZoneId1: string,
    startDateTime2: DateTimeType,
    startDateTimeZoneId2: string,
    endDateTime2: DateTimeType,
    endDateTimeZoneId2: string,
    isInclusive: boolean = false
  ): boolean {
    const compareStart = isInclusive ? (a: DateTimeType, b: DateTimeType) => a <= b : (a: DateTimeType, b: DateTimeType) => a < b;
    const compareEnd = isInclusive ? (a: DateTimeType, b: DateTimeType) => a >= b : (a: DateTimeType, b: DateTimeType) => a > b;

    return compareEnd(dayjs.tz(endDateTime1, endDateTimeZoneId1), dayjs.tz(startDateTime2, startDateTimeZoneId2))
      && compareStart(dayjs.tz(startDateTime1, startDateTimeZoneId1), dayjs.tz(endDateTime2, endDateTimeZoneId2));
  }

  /**
   * Adds the specified amount of time to the given dateTime.
   * @param dateTime - The dateTime value to add time to.
   * @param amount - The amount of time to add.
   * @param unit - The unit of time to add.
   * @returns The dateTime with the added time.
   */
  public static addTime(dateTime: DateTimeType, amount: number, unit: dayjs.ManipulateType): dayjs.Dayjs {
    if (unit === 'hour' || unit === 'hours') {
      return dayjs(dateTime).add(Math.round(amount * 60), 'minute').startOf('minute');
    }

    return dayjs(dateTime).add(amount, unit);
  }

  /**
   * Subtracts the specified amount of time to the given dateTime.
   * @param dateTime - The dateTime value to subtract time to.
   * @param amount - The amount of time to subtract.
   * @param unit - The unit of time to subtract.
   * @returns The dateTime with the subtracted time.
   */
  public static subtractTime(dateTime: DateTimeType, amount: number, unit: dayjs.ManipulateType): dayjs.Dayjs {
    if (unit === 'hour' || unit === 'hours') {
      return dayjs(dateTime).subtract(Math.round(amount * 60), 'minute').startOf('minute');
    }

    return dayjs(dateTime).subtract(amount, unit);
  }

  /**
    * Returns the current Unix epoch time.
    * @returns The current Unix epoch time.
   */
  public static getUnixEpochNow(): number {
    return dayjs().unix();
  }

  /**
   * Sorts an array of dates in ascending or descending order.
   * @param dates - Array of dates to sort. Can be strings, numbers, Date objects, or any valid date format supported by dayjs.
   * @param ascending - Optional boolean parameter to determine sort order. True for ascending (default), false for descending.
   * @returns Sorted array of dates as dayjs objects.
   */
  public sortDates(dates: DateTimeType[], ascending: boolean = true): dayjs.Dayjs[] {
    return dates.map(date => dayjs(date)).sort((a, b) => ascending ? a.isBefore(b) ? -1 : 1 : a.isAfter(b) ? -1 : 1);
  }

  /**
 * Converts a given dateTime into a Unix timestamp in milliseconds.
 * @param dateTime - The datetime value to convert.
 * @returns The Unix timestamp in milliseconds.
 */
  public static convertToUnixMillis(dateTime: DateTimeType): number {
    return dayjs(dateTime).valueOf();
  }
}
